import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { useFinishAppointment } from '@/hooks/useAppointments';
import { useSendInvoiceEmail } from '@/hooks/useInvoices';
import { receptionDeskQueryKey, useReceptionDesk } from '@/hooks/useReceptionDesk';
import { finishToastKey, type FinishOutcome } from '@/lib/finishAppointment';
import {
  DESK_PAYMENT_METHODS,
  splitCoversOutstanding,
  type DeskPaymentMethod,
  type PaymentQueueRow,
} from '@/lib/paymentQueue';
import { formatMoneyCents } from '@/lib/reporting/format';
import { supabase } from '@/lib/supabase';

function formatKrInput(cents: number): string {
  return (cents / 100).toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseKrInput(value: string): number {
  const trimmed = value.trim().replace(/\s/g, '');
  if (!trimmed) return 0;
  const normalized = trimmed.includes(',')
    ? trimmed.replace(/\./g, '').replace(',', '.')
    : trimmed;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100);
}

function when(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Copenhagen',
  }).format(new Date(iso));
}

function SettleRow({
  row,
  onDone,
}: {
  row: PaymentQueueRow;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<DeskPaymentMethod>('card');
  const [amount, setAmount] = useState(formatKrInput(row.outstanding_cents));
  const [second, setSecond] = useState('');
  const [secondMethod, setSecondMethod] = useState<DeskPaymentMethod>('cash');
  const [busy, setBusy] = useState(false);
  const sendInvoiceEmail = useSendInvoiceEmail();

  const amounts = [parseKrInput(amount)];
  if (second.trim()) amounts.push(parseKrInput(second));

  const pay = async () => {
    if (!splitCoversOutstanding(amounts, row.outstanding_cents)) {
      toast.error(t('payment_queue.split_invalid'));
      return;
    }
    setBusy(true);
    try {
      const payments = [
        { method, amount_cents: amounts[0] },
        ...(amounts[1] ? [{ method: secondMethod, amount_cents: amounts[1] }] : []),
      ];
      const { error } = await supabase.rpc('settle_invoice_at_desk', {
        p_invoice_id: row.invoice_id,
        p_payments: payments,
      });
      if (error) throw error;
      toast.success(t('payment_queue.paid'));
      setOpen(false);
      await onDone();
    } catch (err) {
      console.error('desk settlement failed', err);
      toast.error(t('payment_queue.pay_failed'));
    } finally {
      setBusy(false);
    }
  };

  const usePunchCard = async () => {
    setBusy(true);
    try {
      const { error } = await supabase.rpc('settle_invoice_with_punch_card', {
        p_invoice_id: row.invoice_id,
      });
      if (error) throw error;
      toast.success(t('payment_queue.punch_card_done'));
      await onDone();
    } catch (err) {
      console.error('punch card settlement failed', err);
      toast.error(t('payment_queue.punch_card_failed'));
    } finally {
      setBusy(false);
    }
  };

  const payLater = async () => {
    setBusy(true);
    try {
      const { error } = await supabase.rpc('mark_invoice_pay_later', {
        p_invoice_id: row.invoice_id,
      });
      if (error) throw error;
      try {
        await sendInvoiceEmail.mutateAsync({ invoice_id: row.invoice_id });
        toast.success(t('payment_queue.pay_later_sent'));
      } catch (emailError) {
        console.error('invoice email failed', emailError);
        toast.success(t('payment_queue.pay_later_moved'));
      }
      await onDone();
    } catch (err) {
      console.error('pay later failed', err);
      toast.error(t('payment_queue.pay_failed'));
    } finally {
      setBusy(false);
    }
  };

  const [more, setMore] = useState(false);

  if (!open) {
    return (
      <Button type="button" onClick={() => setOpen(true)}>
        {t('payment_queue.take_payment')}
      </Button>
    );
  }

  return (
    <Modal
      open
      title={t('payment_queue.take_payment')}
      onClose={() => setOpen(false)}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={() => void pay()} disabled={busy}>
            {t('payment_queue.take_payment')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-ds-text">{row.patient_name ?? '—'}</p>
        <p className="text-sm text-ds-muted">{row.invoice_number ?? '—'}</p>
      </div>
      <div className="flex gap-2">
        <select
          className="rounded border border-ds-line px-2 py-1"
          value={method}
          onChange={(event) => setMethod(event.target.value as DeskPaymentMethod)}
        >
          {DESK_PAYMENT_METHODS.map((item) => (
            <option key={item} value={item}>
              {t(`payment_queue.method_${item}`)}
            </option>
          ))}
        </select>
        <input
          className="w-24 rounded border border-ds-line px-2 py-1"
          inputMode="decimal"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          aria-label={t('payment_queue.amount')}
        />
      </div>
      <Button type="button" variant="tertiary" onClick={() => setMore((value) => !value)}>
        {t('appointments.override.more')}
      </Button>
      {more ? (
        <>
          <div className="flex gap-2">
            <select
              className="rounded border border-ds-line bg-ds-card px-2 py-1"
              value={secondMethod}
              onChange={(event) => setSecondMethod(event.target.value as DeskPaymentMethod)}
            >
              {DESK_PAYMENT_METHODS.map((item) => (
                <option key={item} value={item}>
                  {t(`payment_queue.method_${item}`)}
                </option>
              ))}
            </select>
            <input
              className="w-24 rounded border border-ds-line bg-ds-card px-2 py-1"
              inputMode="decimal"
              value={second}
              placeholder={t('payment_queue.second_amount')}
              onChange={(event) => setSecond(event.target.value)}
              aria-label={t('payment_queue.second_amount')}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => void payLater()} disabled={busy}>
              {t('payment_queue.pay_later')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => void usePunchCard()} disabled={busy}>
              {t('payment_queue.punch_card')}
            </Button>
          </div>
        </>
      ) : null}
      </div>
    </Modal>
  );
}

export function PaymentQueuePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const canCreate = Boolean(activeClinicId && hasPermission(activeClinicId, 'invoices', 'create'));
  const canTakePayment = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'payments', 'create'),
  );
  const desk = useReceptionDesk(activeClinicId);
  const finish = useFinishAppointment();

  const onFinish = async (appointmentId: string) => {
    try {
      const result = await finish.mutateAsync(appointmentId);
      toast.success(t(finishToastKey(result.outcome as FinishOutcome)));
      if (activeClinicId) {
        await qc.invalidateQueries({ queryKey: receptionDeskQueryKey(activeClinicId) });
      }
    } catch (err) {
      console.error('finish from queue failed', err);
      toast.error(t('appointments.finish.failed'));
    }
  };

  const refresh = async () => {
    if (activeClinicId) {
      await qc.invalidateQueries({ queryKey: receptionDeskQueryKey(activeClinicId) });
    }
  };

  if (desk.isLoading) {
    return <p className="text-ds-muted">{t('payment_queue.loading')}</p>;
  }
  if (desk.error) {
    return <ErrorAlert message={t('payment_queue.load_error')} />;
  }

  const queue = desk.data?.queue ?? [];
  const awaiting = desk.data?.awaiting ?? [];

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-lg font-semibold">{t('payment_queue.title')}</h2>
        <p className="mt-1 text-sm text-ds-muted">{t('payment_queue.lead')}</p>
        {queue.length === 0 ? (
          <p className="mt-4 text-sm text-ds-muted">{t('payment_queue.empty')}</p>
        ) : (
          <table className="mt-4 w-full text-left text-sm">
            <thead className="text-ds-muted">
              <tr>
                <th className="py-2 pr-3">{t('payment_queue.patient')}</th>
                <th className="py-2 pr-3">{t('payment_queue.service')}</th>
                <th className="py-2 pr-3">{t('payment_queue.visit')}</th>
                <th className="py-2 pr-3">{t('payment_queue.invoice')}</th>
                <th className="py-2 pr-3">{t('payment_queue.outstanding')}</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {queue.map((row) => (
                <tr key={row.invoice_id} className="border-t border-ds-line">
                  <td className="py-2 pr-3">{row.patient_name ?? '—'}</td>
                  <td className="py-2 pr-3">{row.service_name ?? '—'}</td>
                  {/* Scheduled start, same clock as the visit. Revenue uses completed_at; the till uses the payment date. */}
                  <td className="py-2 pr-3">{when(row.starts_at)}</td>
                  <td className="py-2 pr-3">{row.invoice_number ?? row.invoice_status}</td>
                  <td className="py-2 pr-3">{formatMoneyCents(row.outstanding_cents)}</td>
                  <td className="py-2 text-right">
                    <div className="mb-2">
                      <Button
                        type="button"
                        variant="tertiary"
                        onClick={() => navigate(`/invoices/${row.invoice_id}`)}
                      >
                        {t('payment_queue.open_invoice')}
                      </Button>
                    </div>
                    {canTakePayment ? <SettleRow row={row} onDone={refresh} /> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold">{t('payment_queue.awaiting_title')}</h2>
        <p className="mt-1 text-sm text-ds-muted">{t('payment_queue.awaiting_lead')}</p>
        {awaiting.length === 0 ? (
          <p className="mt-4 text-sm text-ds-muted">{t('payment_queue.awaiting_empty')}</p>
        ) : (
          <ul className="mt-4 divide-y divide-ds-line">
            {awaiting.map((row) => (
              <li key={row.appointment_id} className="flex items-center justify-between gap-3 py-2">
                <div>
                  <div>{row.patient_name ?? '—'}</div>
                  <div className="text-sm text-ds-muted">
                    {row.service_name ?? '—'} · {when(row.ends_at)}
                  </div>
                </div>
                {canCreate ? (
                  <Button
                    type="button"
                    onClick={() => void onFinish(row.appointment_id)}
                    disabled={finish.isPending}
                  >
                    {t('appointments.finish.action')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
